import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import CustomInput from '../../common/CustomInput/CustomInput';
import { Icon } from '../../common/Icon/Icon';
import Loader from '../../common/Loader/Loader';
import Pager from '../../common/Pager/Pager';
import TableHeader from '../../common/TableHeader/TableHeader';
import { useUserContext } from '../../core/context/user.context';
import useFetch from '../../core/hooks/useFetch';
import { useFormInput } from '../../core/hooks/useFormInput';
import useModal from '../../core/hooks/useModal';
import { User } from '../../core/models';

const BASE_URL = process.env.REACT_APP_SC_API_BASE;
const FILTERS = ['email', 'name', 'role', 'vefified', 'status'];
const COLS: {
  label: string;
  sort?: string;
  align?: string;
}[] = [
  {
    label: 'Email',
    sort: 'email',
  },
  {
    label: 'Name',
    sort: 'name',
  },
  {
    label: 'Role',
    sort: 'role',
  },
  {
    label: 'Verified',
    sort: 'verified',
  },
  {
    label: 'Status',
    sort: 'status',
  },
  {
    label: 'Actions',
    align: 'right',
  },
];

function Users() {
  const { user } = useUserContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const timeout = useRef<ReturnType<typeof setInterval> | null>(null);
  const [activeEmail, setActiveEmail] = useState<string | null>(null);
  const page = useMemo(() => searchParams.get('page') || '1', [searchParams]);
  const searchValue = useMemo(() => searchParams.get('search') || '', [searchParams]);
  const search = useFormInput(searchValue || '');
  const sortValue = useMemo(() => searchParams.get('sort') || '', [searchParams]);
  const [sort, setSort] = useState<{
    property: string;
    order: boolean;
    changed: boolean;
  }>({
    property: sortValue.split('-')[0] || '',
    order: sortValue.split('-')[1] !== 'DESC',
    changed: !!sortValue,
  });
  const { result, doFetch, loading, error } = useFetch(
    `${BASE_URL}/admin/users?per_page=12&page=${page}${
      sort.property ? `&sort=${sort.property}${sort.order ? ':ASC' : ':DESC'}` : ''
    }${!search.value ? '' : FILTERS.reduce((str, value) => `${str}&${value}=${search.value}`, '')}`,
  );
  const { modal, open, close } = useModal();
  // TODO: add delete user (isRemoved) endpoint
  const {
    result: deleteResult,
    doFetch: doDeleteFetch,
    // loading: deleteLoading,
    // error: deleteError,
  } = useFetch(`${BASE_URL}/user/endpoint-here?email=${activeEmail}`);

  useEffect(() => {
    doFetch({
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    });
  }, [searchParams]);

  const deleteUser = useCallback((email: string, btnRef: HTMLButtonElement) => {
    setActiveEmail(email);
    open(btnRef);
  }, []);

  const cancelDelete = useCallback(() => {
    setActiveEmail(null);
    close();
  }, []);

  const confirmDelete = useCallback(() => {
    doDeleteFetch({
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    });
    setActiveEmail(null);
    close();
  }, [activeEmail]);

  useEffect(() => {
    if (!deleteResult) return;
    doFetch({
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    });
  }, [deleteResult]);

  useEffect(() => {
    if (timeout.current) clearTimeout(timeout.current);
    timeout.current = setTimeout(() => {
      if (search.value) {
        searchParams.set('search', search.value);
      } else {
        searchParams.delete('search');
      }
      searchParams.delete('page');
      setSearchParams(searchParams);
    }, 500);
    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
  }, [search.value]);

  useEffect(() => {
    if (sort.property && sort.changed) {
      searchParams.set('sort', `${sort.property}${sort.order ? '-ASC' : '-DESC'}`);
    } else {
      searchParams.delete('sort');
    }
    searchParams.delete('page');
    setSearchParams(searchParams);
  }, [sort]);

  const changeSort = useCallback(
    (val: string | undefined) => {
      if (val === sort.property)
        return setSort((prevState) => ({
          ...prevState,
          order: !prevState.order,
          changed: true,
        }));
      return setSort(() => ({
        property: val || '',
        order: true,
        changed: true,
      }));
    },
    [sort],
  );

  return (
    <section>
      <div className="wrap">
        <div className="table-header">
          <CustomInput id="search" name="search" placeholder="Search" input={search} />
        </div>
        {!result ? (
          loading ? (
            <Loader size={32} bgColor="#f2fbfd" />
          ) : error ? (
            <div>{error.message}</div>
          ) : (
            <div className="fade-in">No Users found!</div>
          )
        ) : (
          <div className="table-container">
            <table>
              <TableHeader cols={COLS} sort={sort} changeSort={changeSort} />
              <tbody>
                {result.data?.items.map((el: User) => (
                  <tr key={el._id}>
                    <td>
                      <Link to={`${el._id}`}>{el.email}</Link>
                    </td>
                    <td>{`${el.firstname} ${el.lastname}`}</td>
                    <td>
                      {el.role === 0
                        ? 'User'
                        : el.role === 1
                        ? 'Moderator'
                        : el.role === 2
                        ? 'Admin'
                        : ''}
                    </td>
                    <td style={{ color: el.verified ? 'green' : 'var(--red)' }}>
                      {el.verified ? 'Verified' : 'Unverified'}
                    </td>
                    <td style={{ color: el.is_removed ? 'var(--red)' : 'green' }}>
                      {el.is_removed ? 'Deleted' : 'Active'}
                    </td>
                    <td>
                      <div className="table-actions">
                        <Link className="table-action table-action--blue" to={`${el._id}`}>
                          <span className="sr-only">Edit user</span>
                          <Icon.Edit className="icon-small" />
                        </Link>
                        <button
                          className="table-action table-action--red"
                          onClick={(e) => deleteUser(el.email, e.currentTarget)}
                          type="button"
                        >
                          <span className="sr-only">Delete user</span>
                          <Icon.Bin className="icon-small" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {result?.data?.items?.length && result?.data?.pager?.total > 1 ? (
          <Pager total={result?.data?.pager.total} current={+page} />
        ) : null}
      </div>
      {modal({
        children: (
          <div className="confirmation">
            <h2 className="confirmation__title h4">
              Are you shure you want to delete the user: {activeEmail}
            </h2>
            <div className="confirmation__btns">
              <button className="link" onClick={cancelDelete} type="button">
                Cancel
              </button>
              <button className="btn btn--red" onClick={confirmDelete} type="button">
                Delete
              </button>
            </div>
          </div>
        ),
      })}
    </section>
  );
}

export default Users;
